import React, { useState } from 'react';
import { useOnboarding } from './OnboardingSystem';

const WelcomeModal = ({ user, isOpen, onClose }) => {
  const { startOnboarding } = useOnboarding();
  const [currentStep, setCurrentStep] = useState(0);

  if (!isOpen) return null;

  const welcomeSteps = [
    {
      title: `Welcome to Banyan${user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}! 🌳`,
      subtitle: "Your intelligent prompt management platform",
      content: (
        <div className="space-y-6">
          <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
            Banyan helps you create, test, and iterate on AI prompts with intelligent suggestions, A/B testing, and team collaboration.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gradient-to-br from-celadon-50 to-celadon-100 dark:from-celadon-900/20 dark:to-celadon-800/20 rounded-2xl">
              <div className="w-10 h-10 bg-celadon-500 rounded-xl flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Smart Prompts</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Create, test, and iterate with AI-powered suggestions</p>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-2xl">
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Team Collaboration</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Git-like branching, pull requests, and real-time collaboration</p>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-2xl">
              <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Visual Composer</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Drag-and-drop workflow builder for complex AI pipelines</p>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-2xl">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Auto-Testing</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Automated evaluation and A/B testing for your prompts</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "How would you like to get started?",
      subtitle: "Choose your path to success with Banyan",
      content: (
        <div className="space-y-6">
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            We've designed Banyan to work with your workflow. Choose the approach that fits you best:
          </p>
          
          <div className="space-y-4">
            <div className="p-6 border-2 border-celadon-200 dark:border-celadon-700 rounded-2xl hover:border-celadon-400 dark:hover:border-celadon-500 transition-colors cursor-pointer group">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-celadon-100 dark:bg-celadon-800 rounded-xl flex items-center justify-center group-hover:bg-celadon-200 dark:group-hover:bg-celadon-700 transition-colors">
                  <svg className="w-6 h-6 text-celadon-600 dark:text-celadon-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Take the Interactive Tour</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                    Get a guided walkthrough of Banyan's key features with interactive highlights and explanations.
                  </p>
                  <div className="flex items-center text-celadon-600 dark:text-celadon-400 text-sm font-medium">
                    <span>5 minutes • Recommended for first-time users</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-2 border-gray-200 dark:border-gray-700 rounded-2xl hover:border-gray-400 dark:hover:border-gray-500 transition-colors cursor-pointer group">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-gray-600 transition-colors">
                  <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Jump Right In</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                    Start exploring on your own. You'll find helpful hints and tips throughout the interface.
                  </p>
                  <div className="flex items-center text-gray-600 dark:text-gray-400 text-sm font-medium">
                    <span>For experienced users who prefer to explore</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">Pro Tip</p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  You can always access help by clicking the <strong>Help & Documentation</strong> link in your profile menu, or look for the (?) icons throughout the interface.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const currentWelcomeStep = welcomeSteps[currentStep];

  const handleStartTour = () => {
    if (user?.id) {
      localStorage.setItem(`banyan_welcome_seen_${user.id}`, 'true');
    }
    
    const tourSteps = [
      {
        targetId: "stats-sidebar",
        title: "Your Workspace Overview 📊",
        content: "This sidebar shows your workspace metrics and activity. Hover over the icons to see helpful tooltips explaining each metric. Track your projects, prompts, and weekly progress at a glance.",
        position: "right"
      },
      {
        targetId: "block-canvas",
        title: "Your Dashboard Canvas 🎨",
        content: "This is your main workspace made of moveable blocks. You can drag them around, resize them, or add new ones. Press '/' anywhere to add a new block - try it! Each block serves a different purpose in your workflow.",
        position: "top"
      },
      {
        targetId: "help-hint",
        title: "Quick Help & Shortcuts ⚡",
        content: "This hint shows you how to add blocks, and you'll find help icons (?) throughout the interface for contextual assistance. Press '/' to add blocks, and look for tooltips when hovering over UI elements.",
        position: "top"
      },
      {
        targetId: "block-canvas",
        title: "Create Your First Project 🚀",
        content: "Ready to get started? Click on 'Create Project' to create your first project. Projects help you organize prompts by use case, application, or team. Think of them as folders for your AI workflows.",
        position: "top"
      },
      {
        targetId: "block-canvas",
        title: "Add a Composer Block 🔧",
        content: "Once you have a project, press '/' and add a Composer block to start building visual prompt workflows. You can drag and drop different nodes to create complex AI pipelines with testing and evaluation built-in.",
        position: "top"
      },
      {
        targetId: "stats-sidebar",
        title: "You're All Set! 🎉",
        content: "You now know the basics of Banyan! Explore the interface, create projects, and start building amazing prompts. Remember: hover for tooltips, click '?' for help, press '/' to add blocks, and check the documentation anytime.",
        position: "right"
      }
    ];
    
    startOnboarding(tourSteps);
    onClose();
  };

  const handleSkipTour = () => {
    if (user?.id) {
      localStorage.setItem(`banyan_welcome_seen_${user.id}`, 'true');
      localStorage.setItem(`banyan_onboarding_completed_${user.id}`, 'true');
    }
    onClose();
  };

  const handleNextStep = () => {
    if (currentStep < welcomeSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-celadon-50 via-white to-celadon-100 dark:from-celadon-900/20 dark:via-gray-800 dark:to-celadon-800/20 p-8">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5 overflow-hidden">
            <img src="/banyan-icon.jpeg" alt="" className="w-32 h-32 absolute -top-4 -right-4 object-contain transform rotate-12" />
            <img src="/banyan-icon.jpeg" alt="" className="w-20 h-20 absolute bottom-4 left-8 object-contain transform -rotate-12 opacity-50" />
          </div>
          
          <div className="relative z-10">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <img 
                    src="/banyan-icon.jpeg" 
                    alt="Banyan" 
                    className="w-16 h-16 rounded-2xl shadow-lg"
                  />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {currentWelcomeStep.title}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 text-lg">
                    {currentWelcomeStep.subtitle}
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleSkipTour}
                className="flex-shrink-0 p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-white/50 dark:hover:bg-gray-700/50"
                title="Skip welcome"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[60vh]">
          {currentWelcomeStep.content}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div className="flex space-x-2">
              {welcomeSteps.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentStep ? 'bg-celadon-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>
            
            <div className="flex items-center space-x-3">
              {currentStep > 0 && (
                <button
                  onClick={handlePrevStep}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors font-medium"
                >
                  Previous
                </button>
              )}
              
              {currentStep === 0 && (
                <button
                  onClick={handleNextStep}
                  className="px-6 py-3 bg-celadon-500 hover:bg-celadon-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl"
                >
                  Let's Go! →
                </button>
              )}
              
              {currentStep === 1 && (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleSkipTour}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors font-medium"
                  >
                    Skip for now
                  </button>
                  <button
                    onClick={handleStartTour}
                    className="px-6 py-3 bg-celadon-500 hover:bg-celadon-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <span>Start Interactive Tour</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-center mt-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              This welcome guide won't appear again. You can always access help from your profile menu.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal; 